import { create } from "zustand";

// Keyed by each income row's stable useFieldArray id, not its index/position —
// so a delete (a row disappearing) never gets confused with an edit (a
// surviving row's amount changing). See getHasCombinedIncomeChanged below.
type IncomeAmountsById = Record<string, number>;

interface IncomeChangeState {
  driverBaseline?: IncomeAmountsById;
  driverCurrent: IncomeAmountsById;
  secondSignerBaseline?: IncomeAmountsById;
  secondSignerCurrent: IncomeAmountsById;
  setDriverCurrent: (value: IncomeAmountsById) => void;
  setSecondSignerCurrent: (value: IncomeAmountsById) => void;
  commitDriverBaseline: (savedAmounts?: IncomeAmountsById) => void;
  commitSecondSignerBaseline: (savedAmounts?: IncomeAmountsById) => void;
  reset: () => void;
}

// Bridges Signers' live per-row gross-income amounts up to ExitAccountModal,
// which lives outside the Signers form tree (as a ReviewPage sibling) and has
// no direct access to the RHF instance. Each row's baseline auto-seeds from
// the first amount observed for its id (the as-loaded/as-saved value, or 0 for
// a freshly-added blank row) and only moves again once Signers confirms a
// successful save, so Save & Exit can detect an unsaved income *edit* and warn
// before it recalculates the advance in Step 2 — deleting a row entirely must
// NOT trigger this warning (CAPS2 parity: the legacy popup only fires from the
// income-calculator recompute on an amount/frequency edit, never on delete).
export const useIncomeChangeStore = create<IncomeChangeState>((set) => ({
  driverBaseline: undefined,
  driverCurrent: {},
  secondSignerBaseline: undefined,
  secondSignerCurrent: {},
  setDriverCurrent: (value) =>
    set((state) => ({
      driverCurrent: value,
      // Existing baseline entries win (so an edit is still detectable);
      // any row id not seen before adopts its first-observed value as its
      // baseline (so merely adding/removing a row is never "a change").
      driverBaseline: { ...value, ...state.driverBaseline },
    })),
  setSecondSignerCurrent: (value) =>
    set((state) => ({
      secondSignerCurrent: value,
      secondSignerBaseline: { ...value, ...state.secondSignerBaseline },
    })),
  commitDriverBaseline: (savedAmounts) =>
    set((state) => ({ driverBaseline: { ...(savedAmounts ?? state.driverCurrent) } })),
  commitSecondSignerBaseline: (savedAmounts) =>
    set((state) => ({ secondSignerBaseline: { ...(savedAmounts ?? state.secondSignerCurrent) } })),
  reset: () =>
    set({
      driverBaseline: undefined,
      driverCurrent: {},
      secondSignerBaseline: undefined,
      secondSignerCurrent: {},
    }),
}));

/**
 * Whether any row still present in `current` has an amount differing from its
 * own baseline. A row that only exists in `baseline` (i.e. was deleted) is
 * simply absent from `current` and is silently excluded — never counted as a
 * change.
 * @param baseline - Per-row amounts as of the last load/save, keyed by row id.
 * @param current - Live per-row amounts, keyed by row id.
 * @returns {boolean} True when a still-present row's amount differs from its baseline.
 */
function hasAnySurvivingRowChanged(baseline: IncomeAmountsById | undefined, current: IncomeAmountsById): boolean {
  return Object.entries(current).some(([id, amount]) => baseline?.[id] !== undefined && baseline[id] !== amount);
}

/**
 * Whether any surviving income row's amount has drifted from its baseline (as
 * loaded, as-added, or last-saved) value, for the driver or 2nd signer. Read
 * at Save & Exit click time via `getState()` rather than a hook, since it's a
 * one-off check, not something the modal needs to re-render on.
 * @returns {boolean} True when an existing row's amount changed — never true
 * for a pure row deletion.
 */
export function getHasCombinedIncomeChanged(): boolean {
  const state = useIncomeChangeStore.getState();
  return (
    hasAnySurvivingRowChanged(state.driverBaseline, state.driverCurrent) ||
    hasAnySurvivingRowChanged(state.secondSignerBaseline, state.secondSignerCurrent)
  );
}
